import { useCallback, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  BulkActionBar,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  Table,
  Text,
} from "@marekl/adminer/components";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useRowSelection, useTableSort } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  competitionCrumbs,
  lockedNotice,
  useCompetitionReadOnly,
} from "../../../shell/CompetitionLayout";
import { useCompetition } from "../../../lib/queries/competitions";
import { isFrozen } from "../../../lib/competitionState";
import {
  categorySortKey,
  useCompetitionCategories,
  useGenerateCategories,
  useSplitCategory,
  useStartList,
  type CompetitionCategory,
} from "../../../lib/queries/startlists";
import { MergeCategoriesModal } from "../MergeCategoriesModal";
import styles from "./StartListsPage.module.css";

const CATEGORY_FORMS = ["kategorie", "kategorie", "kategorií"] as const;

/**
 * Soutěžní kategorie — what actually runs at this competition, and the
 * one place thin fields are merged.
 *
 * It comes before the start list because the list is built out of these:
 * one discipline block per category, and merging afterwards would have to
 * regroup a list somebody had already arranged. The counts are what the
 * decision is made on, which is why they are summed across the rounds the
 * grouping applies to.
 */
export function StartListsPage() {
  const { id } = useParams();
  const compId = id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: competition, isLoading, error, refetch } = useCompetition(compId);
  // Dokončeno or archived: the categories are frozen with the competition.
  // A competitions_viewer reads them the same way.
  const readOnly = useCompetitionReadOnly();
  const frozen = isFrozen(competition) || readOnly;
  // „Zveřejněno" (not the návrh): the server refuses a merge or split
  // from here on (R16), so the page stops offering one.
  const { data: startList } = useStartList(compId);
  const listPublished = startList?.published === true;
  const regroupLocked = frozen || listPublished;
  const {
    data: list,
    isLoading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCompetitionCategories(compId);
  const categories = list?.rows ?? [];
  const missing = list?.missing ?? 0;

  const generateCategories = useGenerateCategories(compId);
  const split = useSplitCategory(compId);

  // The library owns the selection now: the checkbox column, the header
  // select-all (which this page never had) and the indeterminate state all
  // come from `sel.selection`.
  const sel = useRowSelection(categories, (c) => c.id);
  // Pulled out because the callback below depends on them: the hook and
  // the mutation both hand back a fresh object every render, while these
  // two members are stable.
  const clearSelection = sel.clear;
  const splitMutate = split.mutate;
  const [mergeOpen, setMergeOpen] = useState(false);

  // Every category of one competition shares a scope, so the first row
  // speaks for all of them.
  const sharedWithSeries = categories[0]?.shared_with_series ?? false;
  const countedCompetitions = categories[0]?.counted_competitions ?? 1;
  const mergedCount = categories.filter((c) => c.merged).length;

  // Hoisted, with its closures in useCallback, because useTableSort
  // re-sorts whenever the columns identity changes — an inline array
  // would re-sort on every render (AGENTS.md §5).
  //
  // The deps are the STABLE members, not the objects holding them:
  // useRowSelection returns a fresh literal every render and a
  // useMutation result is not identity-stable either, so depending on
  // `sel` and `split` would have rebuilt this callback — and with it the
  // columns, and with them the sort — on every render, under a comment
  // saying it did not.
  const onSplit = useCallback(
    (category: CompetitionCategory) =>
      splitMutate(category.id, {
        onSuccess: () => {
          clearSelection();
          notifySuccess(`„${category.name}“ rozděleno zpět`);
        },
        onError: (err) => notifyError(err),
      }),
    [splitMutate, clearSelection],
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Soutěžní kategorie",
        sortable: true,
        // NOT the heading. Sorted as text it files „Duo/trio" before
        // „Sólo" and „DĚTI" before „SENIOR", because the heading is
        // composed to be read, not to be ordered. The five keys are the
        // federation's own order, and they are packed into one string
        // because sortValue is a single comparable — the offset keeps a
        // negative sort_order from breaking the padding.
        sortValue: (c: CompetitionCategory) => categorySortKey(c.order),
        render: (c: CompetitionCategory) => (
          <>
            {c.name}
            {c.merged && (
              <>
                {" "}
                <Badge variant="info">sloučeno z {c.discipline_category_ids.length}</Badge>
              </>
            )}
          </>
        ),
      },
      {
        key: "entries",
        header: "Na této soutěži",
        numeric: true,
        sortable: true,
        sortValue: (c: CompetitionCategory) => c.local_entry_count,
        tdClass: styles.colCount,
        render: (c: CompetitionCategory) => c.local_entry_count,
      },
      // Only worth a column when the two can differ: on a pohárovka the
      // scope is the competition itself, so a second column would repeat
      // the first.
      ...(sharedWithSeries
        ? [
            {
              key: "entriesAll",
              header: "Napříč všemi koly",
              numeric: true,
              sortable: true,
              sortValue: (c: CompetitionCategory) => c.entry_count,
              tdClass: styles.colCount,
              render: (c: CompetitionCategory) => c.entry_count,
            },
          ]
        : []),
      {
        key: "actions",
        header: "",
        tdClass: styles.colActions,
        render: (c: CompetitionCategory) =>
          c.merged && !regroupLocked ? (
            <Table.Actions>
              <Table.Action appearance="button" onClick={() => onSplit(c)}>
                Rozdělit
              </Table.Action>
            </Table.Actions>
          ) : null,
      },
    ],
    [sharedWithSeries, onSplit, regroupLocked],
  );

  // The server already returns this order; the default reproduces it, so
  // clicking a header twice comes back to what the page opened with. The
  // choice rides in the URL like every other sorted table here, which
  // makes a reordered list a link.
  const tableSort = useTableSort(categories, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "name", dir: "asc" },
  });

  const crumbs = competitionCrumbs(compId, competition, { label: "Soutěžní kategorie" });

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  // Both handlers clear the selection when they actually change the row set:
  // useRowSelection deliberately never prunes against `rows` (an in-flight
  // refetch returning [] would wipe a selection the user still has on
  // screen), so a key whose row these mutations remove would otherwise
  // linger — the bar counting 2 with one visible tick, and "Sloučit vybrané"
  // enabled over a single id. A build that changed nothing removes no row,
  // so it keeps the selection: throwing away a pick the user is still
  // working with, to tidy up after a no-op, is its own small rudeness.
  // (onSplit is the other half and sits above, where the columns need it.)
  const onGenerate = () =>
    generateCategories.mutate(undefined, {
      onSuccess: (res) => {
        if (res.created || res.removed) sel.clear();
        notifySuccess(
          res.created || res.removed
            ? [
                res.created > 0 &&
                  `Doplněno: ${res.created} ${pluralizeCzech(res.created, CATEGORY_FORMS)}.`,
                res.removed > 0 &&
                  `Odebráno: ${res.removed} ${pluralizeCzech(res.removed, CATEGORY_FORMS)}.`,
              ]
                .filter(Boolean)
                .join(" ")
            : "Kategorie odpovídají přihláškám",
        );
      },
      onError: (err) => notifyError(err),
    });

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title="Soutěžní kategorie"
          subtitle={competition.name}
          actions={
            <>
              {!frozen && (
                <Button onClick={onGenerate} loading={generateCategories.isPending}>
                  Doplnit podle přihlášek
                </Button>
              )}
              <Link to={`/soutez/${compId}/startovni-listina`}>
                <Button variant="primary">Startovní listina</Button>
              </Link>
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Text>
            Kategorie, ve kterých se na této soutěži bude soutěžit. Kategorie lze sloučit. Sloučená
            kategorie se hodnotí i vyhlašuje jako jedna. Nelze sloučit kategorie s rozdílnými
            pravidly.{" "}
            {sharedWithSeries && (
              <strong>
                Seskupení platí pro celou sérii; počty přihlášek jsou sečtené přes{" "}
                {countedCompetitions}{" "}
                {pluralizeCzech(countedCompetitions, ["kolo", "kola", "kol"])} stejného typu.
              </strong>
            )}
          </Text>

          {/* The same drift the start list reports, one step earlier: a
              přihláška in a combination nobody has generated a category
              for is simply absent from the screen that decides what runs.
              Absent, not wrong — which is why it needs saying. */}
          {frozen && <Alert variant="info">{lockedNotice(competition, readOnly)}</Alert>}
          {!frozen && listPublished && (
            <Alert variant="info">
              Startovní listina je zveřejněná, takže kategorie už nejde sloučit ani rozdělit.
            </Alert>
          )}

          {missing > 0 && !frozen && (
            <Alert variant="warn">
              <strong>Soutěžní kategorie neodpovídají přihláškám.</strong>
              <p className={styles.missingText}>
                {missing === 1
                  ? "Jedna přihlášená kombinace tu ještě nemá svou kategorii."
                  : `${missing} ${pluralizeCzech(missing, [
                      "přihlášená kombinace tu ještě nemá",
                      "přihlášené kombinace tu ještě nemají",
                      "přihlášených kombinací tu ještě nemá",
                    ])} svou kategorii.`}{" "}
                Doplňte je tlačítkem{" "}
                <Button
                  size="sm"
                  variant="primary"
                  loading={generateCategories.isPending}
                  onClick={onGenerate}
                >
                  Doplnit podle přihlášek
                </Button>
              </p>
            </Alert>
          )}

          {/* No Card: the table IS the content of this page, and a card
              around the only thing on screen is a border drawn for its own
              sake. Cards earn their keep when several of them sit side by
              side. */}
          <Table<CompetitionCategory>
            framed
            {...tableSort.tableProps}
            rowKey={(c) => c.id}
            isLoading={categoriesLoading}
            error={categoriesError}
            onRetry={refetchCategories}
            empty={
              <EmptyState
                title="Zatím žádné kategorie"
                description="Kategorie se odvodí z podaných přihlášek. Pokud soutěž přihlášky má, doplňte je tlačítkem výše."
              />
            }
            selection={regroupLocked ? undefined : { ...sel.selection, label: (c) => c.name }}
            columns={columns}
          />

          {/* Sticky in flow, directly after the table it acts on — the library
              owns the geometry, so nothing here needs to know the sidebar's
              width or the topbar's height. */}
          {!regroupLocked && (
          <BulkActionBar
            count={sel.selectedCount}
            onClear={sel.clear}
            nounForms={["kategorie", "kategorie", "kategorií"]}
          >
            <Button
              size="sm"
              variant="primary"
              // Merging needs something to merge WITH, so one row is not
              // enough. The bar still appears at one, which is how the reader
              // learns the selection exists and how to drop it — the old
              // Alert only showed up at two and left a single tick stranded.
              disabled={sel.selectedCount < 2}
              onClick={() => setMergeOpen(true)}
            >
              Sloučit vybrané
            </Button>
          </BulkActionBar>
          )}

          {mergedCount > 0 && !regroupLocked && (
            <Text variant="muted">
              Sloučených kategorií: {mergedCount}. Sloučení jde kdykoliv vrátit tlačítkem
              „Rozdělit“.
            </Text>
          )}
        </Page.Content>
      </Page>

      <MergeCategoriesModal
        open={mergeOpen}
        competitionId={compId}
        categoryIds={sel.selectedRows.map((c) => c.id)}
        onClose={() => setMergeOpen(false)}
        onMerged={(row) => {
          setMergeOpen(false);
          sel.clear();
          notifySuccess(`Sloučeno do „${row.name}“`);
        }}
      />
    </>
  );
}
